import { requestAssetApi } from './getAssetById';
import { normalizeAssetLookupKey } from '../utils/bookingAssetThumbnailCache';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function pickString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }
  return null;
}

function parseTimestampMs(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 1e12 ? Math.round(value * 1000) : Math.round(value);
  }
  if (typeof value === 'string' && value.trim()) {
    const ms = Date.parse(value.trim());
    if (Number.isFinite(ms)) {
      return ms;
    }
  }
  return null;
}

/** One favourite row from GET `/favourites` (IDs today; asset payload if API enriches later). */
export type FavouriteWireRow = {
  assetId: string;
  savedAt: number | null;
  asset: Record<string, unknown> | null;
};

function unwrapFavouritesListPayload(raw: unknown): unknown[] {
  if (Array.isArray(raw)) {
    return raw;
  }
  if (!isRecord(raw)) {
    return [];
  }
  for (const key of [
    'favourites',
    'favorites',
    'items',
    'data',
    'results',
    'rows',
    'records',
  ] as const) {
    const nested = raw[key];
    if (Array.isArray(nested)) {
      return nested;
    }
  }
  return [];
}

function parseFavouriteWireRow(row: unknown): FavouriteWireRow | null {
  if (typeof row === 'string' || typeof row === 'number') {
    const assetId = pickString(row);
    return assetId ? { assetId, savedAt: null, asset: null } : null;
  }
  if (!isRecord(row)) {
    return null;
  }
  const assetId = pickString(
    row.asset_id,
    row.assetId,
    row.AssetId,
    row.id,
    row.Id,
    row.favourite_asset_id,
    row.favorite_asset_id,
  );
  if (!assetId) {
    return null;
  }
  const nestedAsset =
    (isRecord(row.asset) ? row.asset : null) ??
    (isRecord(row.Asset) ? row.Asset : null) ??
    null;
  const nestedId = nestedAsset
    ? pickString(nestedAsset.asset_id, nestedAsset.assetId, nestedAsset.id)
    : null;
  const savedAt =
    parseTimestampMs(row.created_at) ??
    parseTimestampMs(row.createdAt) ??
    parseTimestampMs(row.saved_at) ??
    parseTimestampMs(row.savedAt) ??
    null;
  /** Whole row looks like an asset record (has name / images) — keep as snapshot. */
  const looksLikeAsset =
    nestedAsset == null &&
    (typeof row.asset_name === 'string' ||
      typeof row.assetName === 'string' ||
      Array.isArray(row.images) ||
      typeof row.asset_image_url === 'string');
  return {
    assetId: nestedId ?? assetId,
    savedAt,
    asset: nestedAsset ?? (looksLikeAsset ? row : null),
  };
}

/** Normalize GET `/favourites` wire JSON into ordered unique rows. */
export function parseFavouritesList(raw: unknown): FavouriteWireRow[] {
  const seen = new Set<string>();
  const out: FavouriteWireRow[] = [];
  for (const item of unwrapFavouritesListPayload(raw)) {
    const row = parseFavouriteWireRow(item);
    if (!row) {
      continue;
    }
    const key = normalizeAssetLookupKey(row.assetId.trim());
    if (!key || seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push(row);
  }
  return out;
}

export function parseIsFavouriteResponse(raw: unknown): boolean {
  if (typeof raw === 'boolean') {
    return raw;
  }
  if (!isRecord(raw)) {
    return false;
  }
  const flag =
    raw.isFavourite ?? raw.isFavorite ?? raw.is_favourite ?? raw.is_favorite ?? raw.favourite ?? raw.favorite;
  if (typeof flag === 'boolean') {
    return flag;
  }
  if (typeof flag === 'string') {
    const s = flag.trim().toLowerCase();
    return s === 'true' || s === '1' || s === 'yes';
  }
  return false;
}

/** GET `/favourites/` — JWT client list. */
export async function fetchFavouritesWire(): Promise<unknown> {
  return requestAssetApi({ method: 'GET', path: '/favourites/' });
}

/** GET `/favourites` — parsed rows. */
export async function fetchFavourites(): Promise<FavouriteWireRow[]> {
  const raw = await fetchFavouritesWire();
  return parseFavouritesList(raw);
}

/** POST `/favourites/:asset_id` — add. */
export async function addFavourite(assetId: string): Promise<void> {
  const id = assetId.trim();
  if (!id) {
    return;
  }
  await requestAssetApi({
    method: 'POST',
    path: `/favourites/${encodeURIComponent(id)}`,
  });
}

/** DELETE `/favourites/:asset_id` — remove. */
export async function removeFavourite(assetId: string): Promise<void> {
  const id = assetId.trim();
  if (!id) {
    return;
  }
  await requestAssetApi({
    method: 'DELETE',
    path: `/favourites/${encodeURIComponent(id)}`,
  });
}

/** GET `/favourites/:asset_id` — `{ isFavourite }`. */
export async function fetchIsFavourite(assetId: string): Promise<boolean> {
  const id = assetId.trim();
  if (!id) {
    return false;
  }
  const raw = await requestAssetApi({
    method: 'GET',
    path: `/favourites/${encodeURIComponent(id)}`,
  });
  return parseIsFavouriteResponse(raw);
}
